import { useEffect, useMemo, useState } from 'react';
import { cn, useMedias } from '@divlab/divanui';

import * as PageProductStore from '@Stores/PageProduct';
import Characteristics from '../Characteristics';
import Tabs from './elements/Tabs';
import QualityLaboratory from '../QualityLaboratory';
import ProductFeatures from '../ProductFeatures';
import useTranslation from '@Queries/useTranslation';
import useDeps from '@Contexts/DI/useDeps';
import styles from './AdditionalInfo.module.css';

import type { FC, HTMLAttributes, ReactElement } from 'react';
import type { ConfiguratorData } from '../..';

interface TabsData {
  id: number;
  label: string;
  component: ReactElement;
  analyticsLabel: string;
}

export interface AdditionalInfoProps extends HTMLAttributes<HTMLDivElement> {
  configurator: ConfiguratorData;
}

const AdditionalInfo: FC<AdditionalInfoProps> = (props) => {
  const { configurator, className, ...restProps } = props;
  const page = PageProductStore.useData();
  const { description, features, qualityLaboratory } = page;
  const { t } = useTranslation();
  const { isMobileM } = useMedias();
  const { analytics } = useDeps();

  const characteristicsTabs = useMemo(() => {
    const tabs = [];
    page.schemeImages?.tags.forEach((tag) => {
      tabs.push({
        ...tag,
        data: page.schemeImages.images.filter((image) => image.tagId === tag.id),
      });
    });

    return tabs;
  }, [page.schemeImages]);

  const headerTabs = useMemo(() => {
    const tabs: TabsData[] = [];
    if (configurator.configuration.parameters) {
      tabs.push({
        id: 1,
        label: t('characteristics'),
        analyticsLabel: 'productParams',
        component: (
          <Characteristics
            parameters={configurator.configuration.parameters}
            title={t('sizes')}
            tabs={characteristicsTabs}
          />
        ),
      });
    }
    if (description || features?.length) {
      tabs.push({
        id: 2,
        label: t('about-model'),
        analyticsLabel: 'modelDescription',
        component: <ProductFeatures description={description} features={features} />,
      });
    }
    if (qualityLaboratory && !isMobileM) {
      tabs.push({
        id: 3,
        label: t('quality-laboratory'),
        analyticsLabel: 'qualityLab',
        component: (
          <QualityLaboratory
            description={qualityLaboratory.description}
            list={qualityLaboratory.list}
            video={qualityLaboratory.video}
            title={qualityLaboratory.title}
          />
        ),
      });
    }

    return tabs;
  }, [
    characteristicsTabs,
    configurator.configuration.parameters,
    description,
    features,
    qualityLaboratory,
    isMobileM,
    t,
  ]);

  const filteredTabs = useMemo(
    () => (isMobileM && headerTabs.length > 2 ? headerTabs.slice(0, -1) : headerTabs),
    [headerTabs, isMobileM],
  );
  const needToShowTabs = filteredTabs.length > 1;

  const [currentTab, setCurrentTab] = useState(filteredTabs[0]);

  const onChangeTab = (id: number) => {
    const selectedTab = filteredTabs.find((tab) => tab.id === id);
    if (selectedTab) {
      analytics.dispatchEvent('product.selectTab', { label: selectedTab.analyticsLabel });
      setCurrentTab(selectedTab);
    }
  };

  useEffect(() => {
    if (!filteredTabs.find((tab) => tab.id === currentTab.id)) {
      setCurrentTab(filteredTabs[0]);
    }
  }, [currentTab.id, filteredTabs]);

  return (
    <div {...restProps} className={cn({ [styles.withoutTabs]: !needToShowTabs }, className)}>
      {needToShowTabs && (
        <Tabs tabs={filteredTabs} className={styles.tabsWrapper} onChangeTab={onChangeTab} />
      )}

      <div className={styles.contentWrapper}>
        {filteredTabs.map((tab) => (
          <div
            key={tab.id}
            className={cn(styles.hidden, { [styles.active]: currentTab.id === tab.id })}
          >
            {tab.component}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdditionalInfo;
